import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCcw } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const usdF = (v) => `$${Number(v || 0).toFixed(4)}`;

export default function AdminLLMUsage() {
  const [days, setDays] = useState(30);
  const [groupBy, setGroupBy] = useState("user");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingCap, setEditingCap] = useState({});  // { user_id: "5.00" }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/llm-usage?days=${days}&group_by=${groupBy}`);
      setData(r.data);
    } catch (e) { toast.error("Failed to load LLM usage"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, groupBy]);

  const saveCap = async (user_id) => {
    const raw = editingCap[user_id];
    const val = raw === "" ? null : parseFloat(raw);
    try {
      await api.put(`/admin/users/${user_id}/llm-limit`, {
        monthly_cost_cap_usd: (Number.isNaN(val) ? null : val),
      });
      toast.success("Cap updated");
      setEditingCap((s) => ({ ...s, [user_id]: undefined }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-llm-usage-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tighter">LLM Usage</h1>
          <div className="vx-label mt-1">
            {days}d ·{" "}
            {data?.totals && (
              <>
                <span className="text-[#e5e7eb]">{data.totals.calls} calls</span>
                {" · "}
                <span className="text-[#3b82f6]">{data.totals.tokens.toLocaleString()} tokens</span>
                {" · "}
                <span className="text-[#f59e0b]">${data.totals.cost_usd.toFixed(4)}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
                    className={`px-3 py-1 rounded border text-xs font-mono uppercase tracking-widest ${
                      days === d ? "border-[#dc2626] text-[#dc2626] bg-[#dc2626]/10" : "border-[#1f2937] text-[#9ca3af] hover:bg-[#1f2937]/40"
                    }`}
                    data-testid={`llm-range-${d}d`}>
              {d}d
            </button>
          ))}
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
                  className="bg-[#0a0e1a] border border-[#1f2937] text-xs font-mono rounded h-8 px-2"
                  data-testid="llm-group-by">
            <option value="user">By user</option>
            <option value="feature">By feature</option>
            <option value="model">By model</option>
          </select>
          <Button size="sm" variant="outline" onClick={load}
                  className="border-[#1f2937] hover:bg-[#1f2937] text-xs h-8" data-testid="llm-refresh">
            <RefreshCcw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>
      </header>

      <div className="rounded-lg border border-[#1f2937] bg-[#111827] overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead className="bg-[#0a0e1a] text-[#6b7280]">
            <tr>
              <th className="text-left px-3 py-2">{groupBy === "user" ? "User" : groupBy === "feature" ? "Feature" : "Model"}</th>
              <th className="text-right px-3 py-2">Calls</th>
              <th className="text-right px-3 py-2">Prompt tokens</th>
              <th className="text-right px-3 py-2">Completion tokens</th>
              <th className="text-right px-3 py-2">Total tokens</th>
              <th className="text-right px-3 py-2">Cost</th>
              {groupBy === "user" && <th className="text-right px-3 py-2">Cap · %</th>}
            </tr>
          </thead>
          <tbody data-testid="llm-usage-table">
            {loading ? (
              <tr><td colSpan={7} className="p-6 text-center text-[#6b7280]">
                <Loader2 className="w-4 h-4 animate-spin inline" /> Loading…
              </td></tr>
            ) : (data?.rows || []).length === 0 ? (
              <tr><td colSpan={7} className="p-6 text-center text-[#6b7280]">No usage recorded in this window.</td></tr>
            ) : (data.rows).map((r) => (
              <tr key={r.key || r.email} className="border-t border-[#1f2937]"
                  data-testid={`llm-row-${r.key || 'row'}`}>
                <td className="px-3 py-2 text-[#e5e7eb]">
                  {groupBy === "user" ? (r.email || "(unknown)") : (r.key || "(none)")}
                </td>
                <td className="px-3 py-2 text-right text-[#9ca3af]">{r.calls}</td>
                <td className="px-3 py-2 text-right text-[#9ca3af]">{r.prompt_tokens.toLocaleString()}</td>
                <td className="px-3 py-2 text-right text-[#9ca3af]">{r.completion_tokens.toLocaleString()}</td>
                <td className="px-3 py-2 text-right text-[#3b82f6]">{r.total_tokens.toLocaleString()}</td>
                <td className="px-3 py-2 text-right text-[#f59e0b] font-bold">{usdF(r.cost_usd)}</td>
                {groupBy === "user" && (
                  <td className="px-3 py-2 text-right">
                    {editingCap[r.key] !== undefined ? (
                      <div className="flex items-center gap-1 justify-end">
                        <Input type="number" step="0.01" min="0"
                               value={editingCap[r.key]}
                               onChange={(e) => setEditingCap((s) => ({ ...s, [r.key]: e.target.value }))}
                               placeholder="unlimited"
                               className="w-20 h-7 bg-[#0a0e1a] border-[#1f2937] font-mono text-xs" />
                        <Button size="sm" onClick={() => saveCap(r.key)}
                                className="bg-[#10b981] hover:bg-[#059669] text-black h-7 text-xs">✓</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingCap((s) => ({ ...s, [r.key]: undefined }))}
                                className="border-[#1f2937] hover:bg-[#1f2937] h-7 text-xs">×</Button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditingCap((s) => ({
                          ...s, [r.key]: r.monthly_cost_cap_usd == null ? "" : String(r.monthly_cost_cap_usd),
                        }))}
                        className="text-xs hover:underline"
                        style={{
                          color: r.pct_of_cap != null && r.pct_of_cap > 80 ? "#ef4444"
                               : r.pct_of_cap != null && r.pct_of_cap > 50 ? "#f59e0b"
                               : "#9ca3af",
                        }}
                        data-testid={`edit-cap-${r.key}`}
                      >
                        {r.monthly_cost_cap_usd == null
                          ? "—"
                          : `$${r.monthly_cost_cap_usd.toFixed(2)} · ${r.pct_of_cap != null ? r.pct_of_cap + "%" : ""}`}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
